import {
  AlertTriangle,
  Bell,
  ChevronRight,
  Download,
  LayoutDashboard,
  Network,
  Package,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { PageData } from "../../api";
import type { AppRoute } from "../../routes";
import { formatValue } from "../../components/DataTable";

export type DashboardSummary = {
  total_sites?: number;
  online?: number;
  offline?: number;
  battery_offline?: number;
  cabinet_offline?: number;
  dg_offline?: number;
  solar_offline?: number;
  io_card_offline?: number;
  sections_enabled?: Record<string, boolean>;
  alarm_types?: Array<{ id?: string; name?: string; count?: number }>;
};

export type DashboardSummaryFilterKey =
  | "online"
  | "offline"
  | "battery_offline"
  | "cabinet_offline"
  | "dg_offline"
  | "solar_offline"
  | "io_card_offline"
  | "";

export function DesignHeader({ route, page }: { route: AppRoute; page: PageData }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-sm font-medium text-slate-500">{route.section}</div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">{page.title}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {page.actions.map((action) => (
            <a
              key={`${action.label}-${action.href}`}
              href={action.href}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
            >
              {action.kind === "download" ? <Download className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {action.label}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StatCards({
  page,
  dashboardMetricRows,
  dashboardTotalRows,
  dashboardSummary,
  sectionsEnabled,
  activeSummaryFilter,
  onSummaryFilter,
}: {
  page: PageData;
  dashboardMetricRows?: Record<string, unknown>[];
  dashboardTotalRows?: number;
  dashboardSummary?: DashboardSummary | null;
  sectionsEnabled?: Record<string, boolean> | null;
  activeSummaryFilter?: DashboardSummaryFilterKey;
  onSummaryFilter?: (key: DashboardSummaryFilterKey) => void;
}) {
  const summary = dashboardSummary || {};
  const se = {
    cabinet: true,
    dg: true,
    battery: true,
    solar: true,
    io_card: true,
    ...(sectionsEnabled || {}),
    ...(summary.sections_enabled || {}),
  };
  // Prefer server summary (full ACL set). Never infer Online/Offline from the current table page alone.
  const totalSites =
    typeof summary.total_sites === "number"
      ? summary.total_sites
      : dashboardTotalRows != null && dashboardTotalRows > 0
        ? dashboardTotalRows
        : page.datasets.reduce((sum, dataset) => sum + (dataset.count ?? dataset.rows.length), 0);
  const hasSummaryCounts =
    typeof summary.online === "number" || typeof summary.offline === "number" || typeof summary.total_sites === "number";
  const online = typeof summary.online === "number" ? summary.online : 0;
  const offline = typeof summary.offline === "number" ? summary.offline : hasSummaryCounts ? Math.max(0, totalSites - online) : 0;
  const cards: Array<{
    id: DashboardSummaryFilterKey | "total" | string;
    label: string;
    value: string | number;
    icon: typeof LayoutDashboard;
    iconBg: string;
    iconColor: string;
    href?: string;
    filterKey?: DashboardSummaryFilterKey;
  }> = [
    {
      id: "total",
      label: "Total Sites",
      value: totalSites,
      icon: LayoutDashboard,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      filterKey: "",
    },
    {
      id: "online",
      label: "Online",
      value: online,
      icon: Network,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      filterKey: "online",
    },
    {
      id: "offline",
      label: "Offline",
      value: offline,
      icon: Bell,
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      filterKey: "offline",
    },
  ];
  if (se.battery !== false) {
    cards.push({
      id: "battery_offline",
      label: "Battery Offline",
      value: summary.battery_offline ?? 0,
      icon: Package,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
      filterKey: "battery_offline",
    });
  }
  if (se.dg !== false) {
    cards.push({
      id: "dg_offline",
      label: "DG Offline",
      value: summary.dg_offline ?? 0,
      icon: Bell,
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      filterKey: "dg_offline",
    });
  }
  if (se.cabinet !== false) {
    cards.push({
      id: "cabinet_offline",
      label: "Cabinet Offline",
      value: summary.cabinet_offline ?? 0,
      icon: Package,
      iconBg: "bg-slate-100",
      iconColor: "text-slate-600",
      filterKey: "cabinet_offline",
    });
  }
  if (se.solar !== false) {
    cards.push({
      id: "solar_offline",
      label: "Solar Offline",
      value: summary.solar_offline ?? 0,
      icon: Package,
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-700",
      filterKey: "solar_offline",
    });
  }
  if (se.io_card !== false) {
    cards.push({
      id: "io_card_offline",
      label: "IO Card Offline",
      value: summary.io_card_offline ?? 0,
      icon: Package,
      iconBg: "bg-violet-100",
      iconColor: "text-violet-700",
      filterKey: "io_card_offline",
    });
  }
  for (const t of summary.alarm_types || []) {
    const id = String(t.id || "").trim();
    const name = String(t.name || id).trim() || "Alarm type";
    if (!id) continue;
    cards.push({
      id: `atype-${id}`,
      label: name,
      value: Number(t.count || 0),
      icon: AlertTriangle,
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      href: `/alarms-active?alarm_type_id=${encodeURIComponent(id)}`,
    });
  }
  return (
    <div className="stats-strip">
      {cards.map((card) => {
        const filterKey = card.filterKey;
        const clickable = Boolean(onSummaryFilter) && filterKey !== undefined && !card.href;
        const active = Boolean(clickable && filterKey && activeSummaryFilter === filterKey);
        const inner = (
          <div className="stat-card-inner">
            <span className={`stat-card-icon ${card.iconBg} ${card.iconColor}`}>
              <card.icon className="h-4 w-4" />
            </span>
            <div className="stat-card-text">
              <div title={card.label}>{card.label}</div>
              <strong>{formatValue(card.value)}</strong>
            </div>
          </div>
        );
        if (card.href) {
          return (
            <Link key={card.id} to={card.href} className="stat-card-ref stat-card-ref--link" title={card.label}>
              {inner}
            </Link>
          );
        }
        if (clickable) {
          return (
            <button
              key={card.id}
              type="button"
              className={`stat-card-ref${active ? " stat-card-ref--active" : ""}`}
              onClick={() => onSummaryFilter?.(filterKey || "")}
              title={active ? "Clear filter" : `Filter table: ${card.label}`}
            >
              {inner}
            </button>
          );
        }
        return (
          <div key={card.id} className="stat-card-ref" title={card.label}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
